/**
 * ItemEditorModal (ITEMS-02) — the full pricebook editor, rendered as a
 * right-side SLIDE-OVER (house pattern; keeps the list visible during pricing
 * work). Every field editable, add-new, archive (soft remove), an image URL for
 * the future proposal builder, and an AI "Auto-fill from website" for the
 * warehouse team.
 *
 * Pickers are MANAGED (ITEMS-03): category / subcategory / manufacturer / vendor
 * are pick-from-our-list-or-add comboboxes sourced from the live catalog — not
 * free text (kills the "GOODMAN" vs "Goodman" vs "goodman " drift ESC let in).
 * Vendor shows the NAME (from mirror_vendor) instead of ESC's bare code.
 *
 * How the big tools do this: ServiceTitan keeps member price as a first-class
 * field, vendor sub-records, and presentation images; Jobber auto-computes
 * markup from cost→price. Pegasus keeps ONE editable price (Seth's B decision);
 * the member (PLUS) price is DERIVED from the plan-discount engine, shown read-
 * only. Markup vs cost is shown for sanity.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Sparkles, Archive, ArchiveRestore, ImageOff, ExternalLink,
  Loader2, Check, AlertCircle, ChevronDown,
} from "lucide-react";
import { SlideOver } from "@/components/ui/SlideOver";
import { Button } from "@/components/ui/Button";
import { ComboBox, stringOpts, type ComboOption } from "@/components/ui/ComboBox";
import { cn } from "@/lib/cn";
import { useEntryFormKeys } from "@/lib/keys";
import { usePlanDiscounts } from "@/features/customer-360/serviceTypes";
import {
  useUpsertItemFull, useArchiveItem, useItemCategories, useManufacturers,
  useSubcategories, useVendors, useItemPurchaseHistory, useVendorRecommendation,
  useAiPrefill, seriesWarranty,
  type Item, type ItemDraft,
} from "./items";
import { usePartVendors } from "@/features/vendors/vendors";

const money = (n: number | null | undefined) =>
  n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

type Draft = ItemDraft & { id?: string };

const blankDraft = (): Draft => ({
  part_number: "", description: null, category: null, subcategory: null,
  part_type: "stock", manufacturer: null, model: null,
  base_price: null, last_purchase: null, average_cost: null,
  taxable: true, inactive: false, qty_in_stock: null, units: "EA",
  warranty_months: null, vendor: null, vendor_part_number: null,
  image_url: null, website_url: null, notes: null,
  equip_type: null, tier: null, efficiency: null, sell_price: null,
  homeowner_overview: null, key_features: null,
  warranty_parts_years: null, warranty_labor_years: null,
});

const fromItem = (it: Item): Draft => ({
  id: it.id, part_number: it.part_number, description: it.description,
  category: it.category, subcategory: it.subcategory, part_type: it.part_type,
  manufacturer: it.manufacturer, model: it.model, base_price: it.base_price,
  last_purchase: it.last_purchase, average_cost: it.average_cost,
  taxable: it.taxable, inactive: it.inactive, qty_in_stock: it.qty_in_stock,
  units: it.units, warranty_months: it.warranty_months, vendor: it.vendor,
  vendor_part_number: it.vendor_part_number, image_url: it.image_url,
  website_url: it.website_url, notes: it.notes,
  equip_type: it.equip_type, tier: it.tier, efficiency: it.efficiency, sell_price: it.sell_price,
  homeowner_overview: it.homeowner_overview, key_features: it.key_features,
  warranty_parts_years: it.warranty_parts_years ?? null, warranty_labor_years: it.warranty_labor_years ?? null,
});

/** PB-ENRICH-01: everything the AI prefill may land in — always EMPTY form
 *  boxes only, always highlighted for review, never saved without the office.
 *  Deliberately absent: tier (office positioning) and every price field. */
const AI_KEYS = [
  "description", "category", "subcategory", "manufacturer", "model", "warranty_months", "notes",
  "equip_type", "efficiency", "key_features", "homeowner_overview", "image_url", "website_url",
] as const;

// Managed equipment types — pick or add (so mini-splits / rooftops slot in later).
const EQUIP_TYPES = ["Furnace", "Air Conditioner", "Heat Pump", "Mini-Split", "Rooftop Unit"];

export function ItemEditorModal({
  item, open, onClose,
}: {
  item: Item | null;   // null = new item
  open: boolean;
  onClose: () => void;
}) {
  const isNew = !item;
  const upsert = useUpsertItemFull();
  const archive = useArchiveItem();
  const { data: plans } = usePlanDiscounts();
  const { data: categories } = useItemCategories();
  const { data: manufacturers } = useManufacturers();
  const { data: vendors } = useVendors();

  const [draft, setDraft] = useState<Draft>(blankDraft);
  const { data: subcats } = useSubcategories(draft.category);
  const { data: partVendors } = usePartVendors(isNew ? null : (item?.part_number ?? null));
  const { data: purchases } = useItemPurchaseHistory(isNew ? null : (item?.part_number ?? null));
  const [askRec, setAskRec] = useState(false);
  const { data: rec, isFetching: recBusy } = useVendorRecommendation(isNew ? null : (item?.part_number ?? null), askRec);
  const [imgBroken, setImgBroken] = useState(false);
  const prefill = useAiPrefill();
  const aiBusy = prefill.isPending;
  const [aiErr, setAiErr] = useState<string | null>(null);
  const [aiKeys, setAiKeys] = useState<Set<string>>(new Set());
  const [aiSrc, setAiSrc] = useState<string | null>(null);
  const [showEsc, setShowEsc] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft(item ? fromItem(item) : blankDraft());
    setImgBroken(false); setAiErr(null); setAiKeys(new Set()); setAiSrc(null); setShowEsc(false); setAskRec(false);
  }, [open, item]);

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));
  const num = (s: string): number | null => (s === "" ? null : Number(s));

  const memberRows = useMemo(() => {
    const bp = draft.base_price;
    if (bp == null || !plans) return [];
    return plans.filter((p) => p.active)
      .map((p) => ({ label: p.label, pct: p.repair_pct, price: bp * (1 - p.repair_pct / 100) }));
  }, [draft.base_price, plans]);

  const markup = useMemo(() => {
    const { base_price: p, last_purchase: c } = draft;
    if (p == null || c == null || c <= 0) return null;
    return Math.round(((p - c) / c) * 100);
  }, [draft.base_price, draft.last_purchase]);

  const vendorOptions: ComboOption[] = useMemo(
    () => (vendors?.list ?? []).map((v) => ({
      value: v.code,
      label: v.name ?? v.code,
      hint: v.name ? `· ${v.code}` : undefined,
    })),
    [vendors],
  );

  const canSave = draft.part_number.trim().length > 0 && !upsert.isPending;
  // QUOTE-POLISH-08: what the series rule would give this model when the pickers are blank.
  const seriesRule = seriesWarranty(draft.manufacturer, draft.model ?? draft.part_number);
  const onFormKeys = useEntryFormKeys(() => { if (canSave) save(); });
  const save = () => {
    if (!canSave) return;
    upsert.mutate({ ...draft, part_number: draft.part_number.trim() }, { onSuccess: () => onClose() });
  };
  const toggleArchive = () => {
    if (!item) return;
    archive.mutate({ id: item.id, archived: !item.archived }, { onSuccess: () => onClose() });
  };

  /** PB-ENRICH-01: the AI fill now works from the part number alone — it finds
   *  the product with a grounded search, reads the product page (the saved
   *  website URL when there is one), and suggests fields + a VERIFIED photo.
   *  It only ever lands in EMPTY boxes, highlighted — the office saves. */
  const canAiFill = !!(draft.part_number.trim() || (draft.model ?? "").trim() || (draft.description ?? "").trim());
  const aiFill = () => {
    if (!canAiFill) { setAiErr("Type a part number, model, or description first — the AI needs a thread to pull."); return; }
    setAiErr(null); setAiSrc(null);
    prefill.mutate(
      {
        part_number: draft.part_number.trim() || null,
        manufacturer: draft.manufacturer,
        model: draft.model,
        description: draft.description,
        equip_type: draft.equip_type,
        website_url: (draft.website_url ?? "").trim() || null,
      },
      {
        onSuccess: (res) => {
          const out: Record<string, unknown> = { ...res.fields };
          // Image + product page ride along as suggestions too.
          if (res.image_url) out.image_url = res.image_url;
          if (!out.website_url && (res.fields.product_url ?? res.source_url)) out.website_url = res.fields.product_url ?? res.source_url;
          const touched = new Set<string>();
          const patch: Record<string, unknown> = {};
          for (const k of AI_KEYS) {
            const cur = (draft as Record<string, unknown>)[k];
            const val = out?.[k];
            const empty = cur == null || cur === "";
            if (empty && val != null && val !== "") {
              patch[k] = k === "warranty_months" ? Number(val) : String(val);
              touched.add(k);
            }
          }
          if (touched.size > 0) setDraft((d) => ({ ...d, ...patch }));
          if (patch.image_url) setImgBroken(false);
          setAiKeys(touched);
          setAiSrc(res.source_url);
          if (!res.found) setAiErr("Couldn't confidently identify this product — check the part number, or paste the product page URL and try again.");
          else if (touched.size === 0) setAiErr("Nothing new to fill — every field it found already has a value.");
        },
        onError: (e) => setAiErr(e instanceof Error ? e.message : "Auto-fill failed."),
      },
    );
  };
  const aiRing = (k: string) => (aiKeys.has(k) ? "border-violet-400 bg-violet-50" : "");
  const srcHost = (() => { try { return aiSrc ? new URL(aiSrc).hostname.replace(/^www\./, "") : null; } catch { return null; } })();

  const footer = (
    <div className="flex items-center gap-2">
      {!isNew && (
        <Button variant="ghost" size="sm" onClick={toggleArchive} disabled={archive.isPending}
          className={item!.archived ? "text-emerald-700" : "text-rose-600"}>
          {item!.archived
            ? <><ArchiveRestore className="h-3.5 w-3.5" /> Restore</>
            : <><Archive className="h-3.5 w-3.5" /> Archive</>}
        </Button>
      )}
      <div className="ml-auto flex items-center gap-2">
        <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
        <Button size="sm" onClick={save} disabled={!canSave}>
          <Check className="h-3.5 w-3.5" />
          {upsert.isPending ? "Saving…" : isNew ? "Add item" : "Save"}
        </Button>
      </div>
    </div>
  );

  return (
    <SlideOver
      open={open} onClose={onClose} size="lg" footer={footer}
      title={isNew ? "New item" : item!.part_number}
      subtitle={isNew ? "Add a part or material to the pricebook" : (item!.description ?? undefined)}
    >
      <div className="space-y-5" data-entry-form onKeyDown={onFormKeys}>
        {!isNew && (
          <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
            <Chip>{item!.source === "esc" ? "From ESC" : "Pegasus item"}</Chip>
            {item!.inactive && <Chip tone="amber">Inactive</Chip>}
            {item!.archived && <Chip tone="rose">Archived</Chip>}
          </div>
        )}

        {/* IDENTITY */}
        <Section title="Identity">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Part #" required>
              <input value={draft.part_number} onChange={(e) => set("part_number", e.target.value)}
                className="inp" placeholder="e.g. 12Y65" />
            </Field>
            <Field label="Part type">
              <input value={draft.part_type ?? ""} onChange={(e) => set("part_type", e.target.value)}
                className="inp" placeholder="stock" />
            </Field>
          </div>
          {/* PB-ENRICH-01: the one-tap catalog fill — type a part # (or model),
              tap, review the highlighted suggestions, save. Empty boxes only. */}
          <div className="rounded-lg border border-violet-200/70 bg-violet-50/40 p-2.5">
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="secondary" size="sm" onClick={aiFill} disabled={aiBusy || !canAiFill}>
                {aiBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 text-violet-500" />}
                {aiBusy ? "Looking it up…" : "AI fill"}
              </Button>
              <span className="min-w-0 flex-1 text-[11px] leading-snug text-slate-500">
                Finds the product from the part # or model — specs, photo, homeowner copy.
                Lands only in <b>empty</b> boxes; you review and save. Never prices.
              </span>
            </div>
            {aiKeys.size > 0 && (
              <p className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px] text-violet-700">
                <Check className="h-3 w-3" /> Filled {aiKeys.size} field{aiKeys.size > 1 ? "s" : ""} (highlighted) — review before saving.
                {aiSrc && srcHost && (
                  <a href={aiSrc} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 font-semibold underline decoration-violet-300 hover:text-violet-900">
                    from {srcHost} <ExternalLink className="h-2.5 w-2.5" />
                  </a>
                )}
              </p>
            )}
            {aiErr && (
              <p className="mt-1.5 flex items-start gap-1.5 text-[11px] text-amber-700"><AlertCircle className="mt-0.5 h-3 w-3 shrink-0" /> {aiErr}</p>
            )}
          </div>
          <Field label="Description">
            <textarea value={draft.description ?? ""} onChange={(e) => set("description", e.target.value || null)}
              rows={2} className={cn("inp resize-none", aiRing("description"))} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category" aiHit={aiKeys.has("category")}>
              <ComboBox value={draft.category ?? null}
                onChange={(v) => { set("category", v); }}
                options={stringOpts(categories ?? [])} placeholder="Pick or add…"
                className={aiRing("category")} />
            </Field>
            <Field label="Subcategory" aiHit={aiKeys.has("subcategory")}>
              <ComboBox value={draft.subcategory ?? null}
                onChange={(v) => set("subcategory", v)}
                options={stringOpts(subcats ?? [])}
                placeholder={draft.category ? "Pick or add…" : "Pick a category first"}
                className={aiRing("subcategory")} />
            </Field>
            <Field label="Manufacturer" aiHit={aiKeys.has("manufacturer")}>
              <ComboBox value={draft.manufacturer ?? null}
                onChange={(v) => set("manufacturer", v)}
                options={stringOpts(manufacturers ?? [])} placeholder="Pick or add…"
                className={aiRing("manufacturer")} />
            </Field>
            <Field label="Model" aiHit={aiKeys.has("model")}>
              <input value={draft.model ?? ""} onChange={(e) => set("model", e.target.value || null)}
                className={cn("inp", aiRing("model"))} />
            </Field>
          </div>
        </Section>

        {/* PRICING */}
        <Section title="Pricing">
          <div className="grid grid-cols-3 gap-3">
            <Field label="Price" hint="What we charge">
              <MoneyInput value={draft.base_price} onChange={(v) => set("base_price", v)} accent />
            </Field>
            <Field label="Cost" hint="What we pay">
              <MoneyInput value={draft.last_purchase} onChange={(v) => set("last_purchase", v)} />
            </Field>
            <Field label="Markup">
              <div className="flex h-[38px] items-center text-sm tabular-nums text-slate-600">
                {markup == null ? <span className="text-slate-300">—</span> : `${markup}%`}
              </div>
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={draft.taxable} onChange={(e) => set("taxable", e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-brand-600" />
            Taxable
          </label>
          {memberRows.length > 0 && (
            <div className="rounded-lg border border-hairline bg-slate-50/60 p-2.5">
              <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                <Sparkles className="h-3 w-3 text-brand-500" /> PLUS member price · auto-applied
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
                {memberRows.map((r) => (
                  <span key={r.label}>
                    {r.label} <span className="text-slate-400">({r.pct}% off)</span>{" "}
                    <strong className="tabular-nums text-emerald-700">{money(r.price)}</strong>
                  </span>
                ))}
              </div>
            </div>
          )}
        </Section>

        {/* EQUIPMENT — makes this item a first-class model in the Good/Better/Best
            quote builder. Filling these is what a "new furnace model" needs to show
            up correctly in a system quote (type, tier, efficiency, install price). */}
        <Section title="Equipment (for system quotes)">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Equipment type" hint="furnace, AC, heat pump…" aiHit={aiKeys.has("equip_type")}>
              <ComboBox value={draft.equip_type ?? null} onChange={(v) => set("equip_type", v)}
                options={stringOpts(EQUIP_TYPES)} placeholder="Pick or add…" className={aiRing("equip_type")} />
            </Field>
            <Field label="Default tier" hint="Good / Better / Best — yours, never AI's">
              <select value={draft.tier ?? ""} onChange={(e) => set("tier", e.target.value || null)} className="inp">
                <option value="">—</option>
                <option value="good">Good</option>
                <option value="better">Better</option>
                <option value="best">Best</option>
              </select>
            </Field>
            <Field label="Efficiency" hint="e.g. 80% AFUE · 16 SEER2" aiHit={aiKeys.has("efficiency")}>
              <input value={draft.efficiency ?? ""} onChange={(e) => set("efficiency", e.target.value || null)}
                className={cn("inp", aiRing("efficiency"))} placeholder="80% AFUE" />
            </Field>
            <Field label="Install price" hint="Price shown in a system quote">
              <MoneyInput value={draft.sell_price} onChange={(v) => set("sell_price", v)} accent />
            </Field>
          </div>
          {/* PB-AI-01 (his Add-Equipment modal): your words for the homeowner.
              When these are empty the proposal's AI writes them live — filled,
              they win outright. */}
          <div className="mt-3 space-y-3">
            <Field label="Homeowner overview" hint="Shown when the homeowner expands this item — empty = the proposal's AI writes it live" aiHit={aiKeys.has("homeowner_overview")}>
              <textarea
                rows={3}
                value={draft.homeowner_overview ?? ""}
                onChange={(e) => set("homeowner_overview", e.target.value || null)}
                placeholder="Persuasive, in your voice — why this unit is worth it…"
                className={cn("inp", aiRing("homeowner_overview"))}
              />
            </Field>
            <Field label="Key features" hint="One per line — feeds the proposal copy either way" aiHit={aiKeys.has("key_features")}>
              <textarea
                rows={3}
                value={draft.key_features ?? ""}
                onChange={(e) => set("key_features", e.target.value || null)}
                placeholder={"Quietest operation in its class\n10-year parts warranty\nTwo-stage heat — even temps"}
                className={cn("inp", aiRing("key_features"))}
              />
            </Field>
          </div>
        </Section>

        {/* STOCK & VENDOR */}
        <Section title="Stock & vendor">
          <div className="grid grid-cols-3 gap-3">
            <Field label="On hand">
              <input type="number" value={draft.qty_in_stock ?? ""}
                onChange={(e) => set("qty_in_stock", num(e.target.value))} className="inp" />
            </Field>
            <Field label="Units">
              <input value={draft.units ?? ""} onChange={(e) => set("units", e.target.value || null)} className="inp" />
            </Field>
            <Field label="Warranty (mo)" aiHit={aiKeys.has("warranty_months")}>
              <input type="number" value={draft.warranty_months ?? ""}
                onChange={(e) => set("warranty_months", num(e.target.value))}
                className={cn("inp", aiRing("warranty_months"))} />
            </Field>
          </div>
          {/* QUOTE-POLISH-07: the model's standard parts / labor warranty — every
              quote line that picks this item starts here (each unit can still be
              changed on the quote). Blank = the tier standard (10/5; Good 5/5). */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Parts warranty (yrs)" hint="what a quote starts with for this model">
              <select value={draft.warranty_parts_years ?? ""} onChange={(e) => set("warranty_parts_years", e.target.value ? Number(e.target.value) : null)} className="inp">
                <option value="">{seriesRule ? `Series standard (${seriesRule.parts} yr)` : "Tier standard"}</option>
                {Array.from({ length: 20 }, (_, i) => i + 1).map((y) => <option key={y} value={y}>{y} yr</option>)}
              </select>
            </Field>
            <Field label="Labor warranty (yrs)" hint={seriesRule ? "Signature 12/7 · Elite 10/5 · Merit 5/2 · Armstrong 10/5" : "no series rule for this brand — set it here"}>
              <select value={draft.warranty_labor_years ?? ""} onChange={(e) => set("warranty_labor_years", e.target.value ? Number(e.target.value) : null)} className="inp">
                <option value="">{seriesRule ? `Series standard (${seriesRule.labor} yr)` : "Tier standard"}</option>
                {Array.from({ length: 20 }, (_, i) => i + 1).map((y) => <option key={y} value={y}>{y} yr</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Vendor" hint={vendors && (vendors.list.some((v) => v.name)) ? undefined : "names sync from ESC"}>
              <ComboBox value={draft.vendor ?? null} onChange={(v) => set("vendor", v)}
                options={vendorOptions} placeholder="Pick a vendor…" />
            </Field>
            <Field label="Vendor part #">
              <input value={draft.vendor_part_number ?? ""}
                onChange={(e) => set("vendor_part_number", e.target.value || null)} className="inp" />
            </Field>
          </div>
        </Section>

        {/* PEGASUS RECOMMENDS — the warehouse purchasing AI. Lazy (button), the
            recommendation is grounded in real VenParts/PO prices server-side. */}
        {!isNew && partVendors && partVendors.length > 0 && (
          <Section title="Pegasus recommends — where to order">
            {!askRec ? (
              <Button variant="secondary" size="sm" onClick={() => setAskRec(true)}>
                <Sparkles className="h-3.5 w-3.5 text-violet-500" /> Recommend a vendor to order from
              </Button>
            ) : recBusy ? (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Comparing vendors…
              </div>
            ) : rec?.recommendation ? (
              <div className="rounded-lg border border-violet-200 bg-violet-50/50 p-3">
                <div className="flex items-baseline justify-between gap-2">
                  <div>
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-violet-500">Order from</span>
                    <div className="text-base font-semibold text-slate-900">
                      {rec.recommendation.vendor_name}
                      <span className="ml-2 font-bold tabular-nums text-emerald-700">{money(rec.recommendation.price)}</span>
                    </div>
                  </div>
                  {rec.recommendation.savings_vs_last != null && rec.recommendation.savings_vs_last > 0 && (
                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                      saves {money(rec.recommendation.savings_vs_last)} vs last
                    </span>
                  )}
                </div>
                <p className="mt-1.5 text-sm text-slate-700">{rec.recommendation.reason}</p>
                {rec.recommendation.watch_outs && (
                  <p className="mt-1 flex items-start gap-1.5 text-xs text-amber-700">
                    <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" /> {rec.recommendation.watch_outs}
                  </p>
                )}
                <p className="mt-1.5 text-[11px] text-slate-400">
                  Compared {rec.vendor_count} vendors · AI suggestion — verify availability before ordering.
                </p>
              </div>
            ) : (
              <p className="text-sm text-slate-400">{rec?.note ?? "No vendor pricing on record for this part."}</p>
            )}
          </Section>
        )}

        {/* VENDOR PRICING — who supplies this part + last cost, cheapest first.
            From the ESC VenParts mirror; hidden until that's synced. */}
        {partVendors && partVendors.length > 0 && (
          <Section title="Vendor pricing — cost compare">
            <div className="overflow-hidden rounded-lg border border-hairline">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-400">
                    <th className="px-2 py-1.5">Vendor</th>
                    <th className="px-2 py-1.5">Their part #</th>
                    <th className="px-2 py-1.5 text-right">Last cost</th>
                    <th className="px-2 py-1.5 text-right">When</th>
                  </tr>
                </thead>
                <tbody>
                  {partVendors.map((pv, i) => (
                    <tr key={pv.vendor_code} className={cn("border-t border-hairline", i === 0 && partVendors.length > 1 && "bg-emerald-50/40")}>
                      <td className="px-2 py-1.5 font-medium text-slate-700">
                        {pv.vendor_name ?? pv.vendor_code}
                        {i === 0 && partVendors.length > 1 && (
                          <span className="ml-1 text-[10px] font-semibold uppercase text-emerald-600">cheapest</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-slate-500">{pv.ven_part ?? "—"}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-slate-600">{money(pv.last_purchase_price)}</td>
                      <td className="px-2 py-1.5 text-right text-slate-400">
                        {pv.date_last_purchase ? new Date(pv.date_last_purchase + "T00:00:00").toLocaleDateString("en-US", { month: "short", year: "numeric" }) : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        )}

        {/* PURCHASE HISTORY — every buy of this part: date, vendor, qty, unit
            cost, PO#. From the ESC PO-line mirror. Track cost over time + who. */}
        {purchases && purchases.length > 0 && (
          <Section title="Purchase history — cost over time">
            <div className="max-h-56 overflow-y-auto rounded-lg border border-hairline">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50">
                  <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400">
                    <th className="px-2 py-1.5">Date</th>
                    <th className="px-2 py-1.5">Vendor</th>
                    <th className="px-2 py-1.5 text-right">Qty</th>
                    <th className="px-2 py-1.5 text-right">Unit cost</th>
                    <th className="px-2 py-1.5 text-right">PO #</th>
                  </tr>
                </thead>
                <tbody>
                  {purchases.slice(0, 60).map((p, i) => (
                    <tr key={`${p.po}-${i}`} className="border-t border-hairline">
                      <td className="whitespace-nowrap px-2 py-1.5 text-slate-500">
                        {p.date ? new Date(p.date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                      </td>
                      <td className="max-w-[160px] truncate px-2 py-1.5 text-slate-700">{p.vendor_name ?? p.vendor_code ?? "—"}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{p.qty}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-slate-700">{money(p.unit_cost)}</td>
                      <td className="px-2 py-1.5 text-right text-slate-400">{p.po.replace(/^0+/, "")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        )}

        {/* IMAGE & WEBSITE */}
        <Section title="Image & website">
          <div className="flex gap-3">
            <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-hairline bg-slate-50">
              {draft.image_url && !imgBroken ? (
                <img src={draft.image_url} alt="" className="h-full w-full object-contain"
                  onError={() => setImgBroken(true)} onLoad={() => setImgBroken(false)} />
              ) : <ImageOff className="h-5 w-5 text-slate-300" />}
            </div>
            <div className="flex-1">
              <Field label="Image URL" hint="Shows in the proposal builder — AI fill only suggests real, load-tested photos" aiHit={aiKeys.has("image_url")}>
                <input value={draft.image_url ?? ""}
                  onChange={(e) => { set("image_url", e.target.value || null); setImgBroken(false); }}
                  className={cn("inp", aiRing("image_url"))} placeholder="https://…/part.jpg" />
              </Field>
            </div>
          </div>
          <Field label="Website URL" hint="Product/spec page — AI fill reads it when set (and finds it when not)" aiHit={aiKeys.has("website_url")}>
            <div className="flex gap-2">
              <input value={draft.website_url ?? ""} onChange={(e) => set("website_url", e.target.value || null)}
                className={cn("inp flex-1", aiRing("website_url"))} placeholder="https://vendor.com/product/…" />
              {draft.website_url && (
                <a href={draft.website_url} target="_blank" rel="noreferrer"
                  className="flex items-center rounded-md border border-hairline px-2 text-slate-500 hover:bg-slate-50" title="Open">
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
            </div>
          </Field>
        </Section>

        {/* NOTES + status */}
        <Section title="Notes">
          <textarea value={draft.notes ?? ""} onChange={(e) => set("notes", e.target.value || null)}
            rows={2} className={cn("inp resize-none", aiRing("notes"))} />
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={!!draft.inactive} onChange={(e) => set("inactive", e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-amber-600" />
            Inactive (kept in the book, not offered for new work)
          </label>
        </Section>

        {/* ESC reference — the original tiers + provenance, collapsed. Honest
            "additional info" without inventing data we don't have. */}
        {!isNew && item!.source === "esc" && (
          <div className="rounded-lg border border-hairline">
            <button type="button" onClick={() => setShowEsc((s) => !s)}
              className="flex w-full items-center justify-between px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-400 hover:bg-slate-50">
              ESC reference
              <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", showEsc && "rotate-180")} />
            </button>
            {showEsc && (
              <div className="space-y-1 border-t border-hairline px-3 py-2 text-xs text-slate-600">
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  <span>ESC A <strong className="tabular-nums">{money(item!.price_a)}</strong></span>
                  <span>ESC B (standard) <strong className="tabular-nums">{money(item!.price_b)}</strong></span>
                  <span>ESC C (PLUS) <strong className="tabular-nums">{money(item!.price_c)}</strong></span>
                </div>
                <p className="text-[11px] text-slate-400">
                  Pegasus uses ONE price (B) and derives the member price automatically — these tiers are
                  ESC's originals, kept for reference. Part # <code>{item!.part_number}</code> is the sync key.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`.inp{width:100%;border:1px solid var(--hairline,#e2e8f0);border-radius:0.375rem;padding:0.45rem 0.55rem;font-size:0.875rem}
      .inp:focus{outline:none;border-color:#6366f1;box-shadow:0 0 0 2px rgb(99 102 241 / 0.25)}`}</style>
    </SlideOver>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2.5">
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{title}</h3>
      {children}
    </section>
  );
}

function Field({
  label, children, hint, required, aiHit,
}: {
  label: string; children: ReactNode; hint?: string; required?: boolean; aiHit?: boolean;
}) {
  return (
    <label className="block text-sm">
      <span className="mb-1 flex items-center gap-1 text-[11px] font-medium text-slate-500">
        {label}{required && <span className="text-rose-500">*</span>}
        {aiHit && <Sparkles className="h-2.5 w-2.5 text-violet-500" />}
        {hint && <span className="font-normal text-slate-300">· {hint}</span>}
      </span>
      {children}
    </label>
  );
}

function MoneyInput({
  value, onChange, accent,
}: {
  value: number | null | undefined; onChange: (v: number | null) => void; accent?: boolean;
}) {
  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
      <input type="number" step="0.01" inputMode="decimal" value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
        className={cn("inp !pl-6 text-right tabular-nums", accent && "border-brand-300 font-semibold")} />
    </div>
  );
}

function Chip({ children, tone }: { children: ReactNode; tone?: "amber" | "rose" }) {
  return (
    <span className={cn(
      "rounded-full px-2 py-0.5 font-medium",
      tone === "amber" ? "bg-amber-100 text-amber-700"
        : tone === "rose" ? "bg-rose-100 text-rose-700"
        : "bg-slate-100 text-slate-600",
    )}>{children}</span>
  );
}
